import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Eye,
  Copy,
  Pencil,
  FileText,
  Building2,
  User,
  Calendar,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Lock,
  MessageCircle,
} from 'lucide-react';
import { getInvoice, downloadInvoicePdf, downloadPDF, shareInvoice } from '../services/api';
import { formatCurrency } from '../utils/currencyFormatter';
import { useEntitlements, Features } from '../hooks/useEntitlements';
import PdfPreviewModal from '../components/PdfPreviewModal';

/**
 * Format date for display
 */
function formatDate(isoString) {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format datetime for display
 */
function formatDateTime(isoString) {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Invoice type display names
 */
const invoiceTypeNames = {
  freelancer: 'Freelancer',
  coach: 'Coach/Consultant',
  gst: 'GST (India)',
  vat: 'VAT Invoice',
  international: 'International',
};

/**
 * Invoice Detail Page
 * 
 * Displays full invoice details from backend-stored data and calculations.
 * Frontend is a pure renderer - no money math, all values from backend.
 */
export default function InvoiceDetailPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Feature entitlements (Feature #15)
  const { isFeatureEnabled } = useEntitlements();
  const canDownloadPdf = isFeatureEnabled(Features.INVOICE_PDF_DOWNLOAD);
  
  // Get back link from state (preserves pagination) or default to /history
  const backTo = location.state?.from || '/history';
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [sharingWhatsApp, setSharingWhatsApp] = useState(false);
  const [preview, setPreview] = useState({ open: false, blobUrl: null, filename: null, loading: false });

  /**
   * Fetch invoice details from backend
   */
  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getInvoice(invoiceId);
      setInvoice(data);
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setError(err.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  /**
   * Handle PDF download
   */
  const handleDownload = async () => {
    setDownloading(true);
    setDownloadSuccess(false);
    
    try {
      const { blob, filename } = await downloadInvoicePdf(invoiceId);
      downloadPDF(blob, filename);
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 3000);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError(`Failed to download PDF: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  /**
   * Handle duplicate invoice action
   * Extracts draft data and navigates to create page with prefill
   */
  const handleDuplicate = () => {
    if (!invoice?.data) return;
    
    const sourceData = invoice.data;
    
    // Build prefill draft - remove derived/computed fields
    // Keep only user-entered fields that the editor expects
    const prefillDraft = {
      // Business info
      business_name: sourceData.business_name || '',
      business_email: sourceData.business_email || '',
      business_phone: sourceData.business_phone || '',
      business_address: sourceData.business_address || '',
      business_city: sourceData.business_city || '',
      business_state: sourceData.business_state || '',
      business_zip: sourceData.business_zip || '',
      business_country: sourceData.business_country || '',
      business_gstin: sourceData.business_gstin || '',
      
      // Client info
      client_name: sourceData.client_name || '',
      client_email: sourceData.client_email || '',
      client_phone: sourceData.client_phone || '',
      client_address: sourceData.client_address || '',
      client_city: sourceData.client_city || '',
      client_state: sourceData.client_state || '',
      client_zip: sourceData.client_zip || '',
      client_country: sourceData.client_country || '',
      client_gstin: sourceData.client_gstin || '',
      
      // Invoice details (dates will be fresh)
      po_number: sourceData.po_number || '',
      notes: sourceData.notes || '',
      terms: sourceData.terms || '',
      currency: sourceData.currency || 'USD',
      tax_rate: sourceData.tax_rate ?? 0,
      discount: sourceData.discount ?? 0,
      
      // GST specific
      place_of_supply: sourceData.place_of_supply || '',
      
      // International bank fields
      bank_name: sourceData.bank_name || '',
      bank_account: sourceData.bank_account || '',
      bank_swift: sourceData.bank_swift || '',
      bank_iban: sourceData.bank_iban || '',
      
      // Line items - convert to editor shape (description/quantity/rate only)
      // Amount is computed by backend, not sent
      items: (sourceData.line_items || []).map(item => ({
        description: item.description || '',
        quantity: item.quantity ?? 1,
        rate: item.rate ?? 0,
        // Do NOT include amount - backend calculates it
      })),
    };
    
    // Navigate to create page with prefill data
    const invoiceType = invoice.invoice_type || 'freelancer';
    navigate(`/create/${invoiceType}`, {
      state: { prefillDraft }
    });
  };

  /**
   * Navigate to invoice editor in edit mode — same invoice number, data prefilled
   */
  const handleEdit = () => {
    const invoiceType = invoice.invoice_type || 'freelancer';
    navigate(`/create/${invoiceType}`, {
      state: {
        editInvoiceId: invoice.id,
        editInvoiceNumber: invoice.invoice_number,
      }
    });
  };

  const handleOpenPreview = async () => {
    setPreview({ open: true, blobUrl: null, filename: `${invoice.invoice_number}.pdf`, loading: true });
    try {
      const { blob, filename } = await downloadInvoicePdf(invoiceId);
      const url = URL.createObjectURL(blob);
      setPreview(prev => ({ ...prev, blobUrl: url, filename, loading: false }));
    } catch (err) {
      console.error('Preview failed:', err);
      setPreview(prev => ({ ...prev, blobUrl: null, loading: false }));
    }
  };

  const handleWhatsAppShare = async () => {
    if (!invoice) return;
    setSharingWhatsApp(true);
    try {
      const { share_token } = await shareInvoice(invoice.id);
      const url = `${window.location.origin}/view/${share_token}`;
      const text = `Hi, please find your invoice ${invoice.invoice_number} here: ${url}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(`Failed to create share link: ${err.message}`);
    } finally {
      setSharingWhatsApp(false);
    }
  };

  const handleClosePreview = () => {
    setPreview(prev => {
      if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
      return { open: false, blobUrl: null, filename: null, loading: false };
    });
  };

  const handlePreviewDownload = () => {
    if (preview.blobUrl && preview.filename) {
      const a = document.createElement('a');
      a.href = preview.blobUrl;
      a.download = preview.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // Get data and calculations from invoice
  const data = invoice?.data || {};
  const calc = invoice?.calculations || {};
  const currency = data.currency || 'USD';
  
  // Line items: use stored calculations for amounts
  const lineItems = calc.line_items || data.line_items || [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3">
            <Link
              to={backTo}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline ml-2">Back to History</span>
            </Link>

            {invoice && (
              <div className="flex items-center gap-1.5 sm:gap-3 overflow-x-auto">
                {/* Edit */}
                <button
                  onClick={handleEdit}
                  title="Edit invoice"
                  className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                >
                  <Pencil className="w-4 h-4" />
                  <span className="hidden sm:inline">Edit</span>
                </button>
                {/* Duplicate */}
                <button
                  onClick={handleDuplicate}
                  title="Duplicate invoice"
                  className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                >
                  <Copy className="w-4 h-4" />
                  <span className="hidden sm:inline">Duplicate</span>
                </button>
                {/* WhatsApp Share */}
                <button
                  onClick={handleWhatsAppShare}
                  disabled={sharingWhatsApp}
                  title="Share via WhatsApp"
                  className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-600 dark:hover:text-green-400 hover:border-green-300 dark:hover:border-green-700 transition-colors disabled:opacity-50 flex-shrink-0"
                >
                  {sharingWhatsApp
                    ? <RefreshCw className="w-4 h-4 animate-spin" />
                    : <MessageCircle className="w-4 h-4" />}
                  <span className="hidden sm:inline">WhatsApp</span>
                </button>
                {/* Preview PDF */}
                {canDownloadPdf && (
                  <button
                    onClick={handleOpenPreview}
                    disabled={preview.loading}
                    title="Preview PDF"
                    className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    {preview.loading
                      ? <RefreshCw className="w-4 h-4 animate-spin" />
                      : <Eye className="w-4 h-4" />}
                    <span className="hidden sm:inline">Preview PDF</span>
                  </button>
                )}
                {/* Download PDF */}
                {canDownloadPdf ? (
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    title="Download PDF"
                    className={`flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors flex-shrink-0 ${
                      downloadSuccess
                        ? 'bg-green-600 text-white'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    } disabled:opacity-50`}
                  >
                    {downloading ? <RefreshCw className="w-4 h-4 animate-spin" /> :
                      downloadSuccess ? <CheckCircle className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                    <span className="hidden sm:inline">
                      {downloading ? 'Downloading...' : downloadSuccess ? 'Downloaded' : 'Download PDF'}
                    </span>
                  </button>
                ) : (
                  <span
                    className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 text-sm font-medium rounded-lg bg-gray-200 dark:bg-gray-800 text-gray-400 cursor-not-allowed flex-shrink-0"
                    title="PDF download not available for your plan"
                  >
                    <Lock className="w-4 h-4" />
                    <span className="hidden sm:inline">Download PDF</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mb-4" />
            <p className="text-gray-600">Loading invoice...</p>
          </div>
        )}

        {/* Invoice Details */}
        {!loading && invoice && (
          <div className="space-y-6">
            {/* Invoice Header Card */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <FileText className="w-6 h-6 text-blue-600" />
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {invoice.invoice_number}
                    </h1>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                    <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                      {invoiceTypeNames[invoice.invoice_type] || invoice.invoice_type}
                    </span>
                    <span className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      Created {formatDateTime(invoice.created_at)}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500 dark:text-gray-500">Total Amount</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(calc.total ?? 0, currency)}
                  </p>
                </div>
              </div>
            </div>

            {/* Business & Client Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Business Info */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                <div className="flex items-center mb-4">
                  <Building2 className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">From</h2>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-gray-900 dark:text-white">{data.business_name || 'N/A'}</p>
                  {data.business_email && <p className="text-gray-600 dark:text-gray-400">{data.business_email}</p>}
                  {data.business_phone && <p className="text-gray-600 dark:text-gray-400">{data.business_phone}</p>}
                  {data.business_address && <p className="text-gray-600 dark:text-gray-400">{data.business_address}</p>}
                  {(data.business_city || data.business_state || data.business_zip) && (
                    <p className="text-gray-600 dark:text-gray-400">
                      {[data.business_city, data.business_state, data.business_zip].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {data.business_country && <p className="text-gray-600 dark:text-gray-400">{data.business_country}</p>}
                  {data.business_gstin && (
                    <p className="text-gray-600 dark:text-gray-400">
                      <span className="font-medium">GSTIN:</span> {data.business_gstin}
                    </p>
                  )}
                </div>
              </div>

              {/* Client Info */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                <div className="flex items-center mb-4">
                  <User className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Bill To</h2>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-gray-900 dark:text-white">{data.client_name || 'N/A'}</p>
                  {data.client_email && <p className="text-gray-600 dark:text-gray-400">{data.client_email}</p>}
                  {data.client_phone && <p className="text-gray-600 dark:text-gray-400">{data.client_phone}</p>}
                  {data.client_address && <p className="text-gray-600 dark:text-gray-400">{data.client_address}</p>}
                  {(data.client_city || data.client_state || data.client_zip) && (
                    <p className="text-gray-600 dark:text-gray-400">
                      {[data.client_city, data.client_state, data.client_zip].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {data.client_country && <p className="text-gray-600 dark:text-gray-400">{data.client_country}</p>}
                  {data.client_gstin && (
                    <p className="text-gray-600 dark:text-gray-400">
                      <span className="font-medium">GSTIN:</span> {data.client_gstin}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Invoice Dates */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-500 mb-1">Invoice Date</p>
                  <p className="font-medium text-gray-900 dark:text-white">{formatDate(data.invoice_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-500 mb-1">Due Date</p>
                  <p className="font-medium text-gray-900 dark:text-white">{formatDate(data.due_date)}</p>
                </div>
                {data.po_number && (
                  <div>
                    <p className="text-gray-500 dark:text-gray-500 mb-1">PO Number</p>
                    <p className="font-medium text-gray-900 dark:text-white">{data.po_number}</p>
                  </div>
                )}
                {data.place_of_supply && (
                  <div>
                    <p className="text-gray-500 dark:text-gray-500 mb-1">Place of Supply</p>
                    <p className="font-medium text-gray-900 dark:text-white">{data.place_of_supply}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Line Items Table */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Line Items</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Rate
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {lineItems.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {item.description || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 text-right">
                          {item.quantity ?? 0}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 text-right">
                          {formatCurrency(item.rate ?? 0, currency)}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white text-right">
                          {formatCurrency(item.amount ?? 0, currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals Section */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(calc.subtotal ?? 0, currency)}
                  </span>
                </div>

                {(calc.discount ?? 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Discount</span>
                    <span className="font-medium text-red-600 dark:text-red-400">
                      -{formatCurrency(calc.discount ?? 0, currency)}
                    </span>
                  </div>
                )}

                {/* GST Tax Breakdown */}
                {calc.tax_breakdown && (
                  <>
                    {calc.tax_breakdown.cgst != null && calc.tax_breakdown.cgst > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">CGST</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(calc.tax_breakdown.cgst, currency)}
                        </span>
                      </div>
                    )}
                    {calc.tax_breakdown.sgst != null && calc.tax_breakdown.sgst > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">SGST</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(calc.tax_breakdown.sgst, currency)}
                        </span>
                      </div>
                    )}
                    {calc.tax_breakdown.igst != null && calc.tax_breakdown.igst > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">IGST</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(calc.tax_breakdown.igst, currency)}
                        </span>
                      </div>
                    )}
                    {calc.tax_breakdown.total_tax != null && calc.tax_breakdown.total_tax > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Tax Total</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(calc.tax_breakdown.total_tax, currency)}
                        </span>
                      </div>
                    )}
                  </>
                )}

                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">Total</span>
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(calc.total ?? 0, currency)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes & Terms */}
            {(data.notes || data.terms) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {data.notes && (
                  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Notes</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{data.notes}</p>
                  </div>
                )}
                {data.terms && (
                  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Terms & Conditions</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{data.terms}</p>
                  </div>
                )}
              </div>
            )}

            {/* Bank Details (International) */}
            {(data.bank_name || data.bank_account || data.bank_swift || data.bank_iban) && (
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Bank Details</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {data.bank_name && (
                    <div>
                      <p className="text-gray-500 dark:text-gray-500 mb-1">Bank Name</p>
                      <p className="font-medium text-gray-900 dark:text-white">{data.bank_name}</p>
                    </div>
                  )}
                  {data.bank_account && (
                    <div>
                      <p className="text-gray-500 dark:text-gray-500 mb-1">Account Number</p>
                      <p className="font-medium text-gray-900 dark:text-white">{data.bank_account}</p>
                    </div>
                  )}
                  {data.bank_swift && (
                    <div>
                      <p className="text-gray-500 dark:text-gray-500 mb-1">SWIFT Code</p>
                      <p className="font-medium text-gray-900 dark:text-white">{data.bank_swift}</p>
                    </div>
                  )}
                  {data.bank_iban && (
                    <div>
                      <p className="text-gray-500 dark:text-gray-500 mb-1">IBAN</p>
                      <p className="font-medium text-gray-900 dark:text-white">{data.bank_iban}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Not Found State */}
        {!loading && !invoice && !error && (
          <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
            <FileText className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Invoice Not Found</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">The invoice you're looking for doesn't exist.</p>
            <Link
              to="/history"
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to History
            </Link>
          </div>
        )}
      </main>

      {/* PDF Preview Modal */}
      {preview.open && (
        <PdfPreviewModal
          blobUrl={preview.blobUrl}
          filename={preview.filename}
          loading={preview.loading}
          onClose={handleClosePreview}
          onDownload={preview.blobUrl ? handlePreviewDownload : null}
        />
      )}
    </div>
  );
}
